import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AdCampaign,
  AdCreativeVersion,
  addAdCreative,
  setAdDailyBudget,
} from '../../api/ads/ads.api';
import { digitsOnly, formatCa, isCampaignEnded, parseWholeCa } from '../../utils/ads-automation';
import AdCreativeForm, { CreativeFields } from './ad-creative-form';
import { runsOnHomeBanner, runsOnPreRoll } from '../../api/ads/ad-placements';
import AdPreview from './ad-preview';

const inputCls =
  'mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text';
const MIN_DAILY = 10000;

interface Props {
  campaign: AdCampaign;
  pending?: AdCreativeVersion | null;
  active?: AdCreativeVersion | null;
  onCampaignChange: (c: AdCampaign) => void;
  onCreativeSent: () => void;
}

const AdBudgetCreativePanel: React.FC<Props> = ({
  campaign,
  pending,
  active,
  onCampaignChange,
  onCreativeSent,
}) => {
  const [daily, setDaily] = useState(campaign.dailyBudgetCa != null ? String(campaign.dailyBudgetCa) : '');
  const [savingBudget, setSavingBudget] = useState(false);
  const budgetLock = useRef(false);

  const base: CreativeFields = {
    title: active?.title || campaign.creative?.title || '',
    body: active?.body || campaign.creative?.body || '',
    imageUrl: active?.imageUrl || campaign.creative?.imageUrl || '',
    videoUrl: active?.videoUrl || campaign.creative?.videoUrl || '',
    landingUrl: active?.landingUrl || campaign.creative?.landingUrl || '',
    ctaText: active?.ctaText || campaign.creative?.ctaText || '',
  };
  const [creative, setCreative] = useState<CreativeFields>(base);
  const [sending, setSending] = useState(false);
  const sendLock = useRef(false);

  const ceiling = Math.max(0, campaign.totalBudgetCa - campaign.spentCa);
  const ended = isCampaignEnded(campaign.status);

  const saveBudget = async (value: number | null) => {
    if (budgetLock.current) return;
    if (value !== null) {
      if (!Number.isSafeInteger(value) || value < MIN_DAILY)
        return void toast.error(`Ngân sách ngày tối thiểu ${formatCa(MIN_DAILY)}.`);
      if (value > ceiling)
        return void toast.error(`Ngân sách ngày không vượt quá ngân sách còn lại (${formatCa(ceiling)}).`);
    }
    budgetLock.current = true;
    setSavingBudget(true);
    try {
      const c = await setAdDailyBudget(campaign.id, value);
      // Chỉ lấy ngân sách ngày từ kết quả, giữ nguyên các phần khác của chiến dịch (nội dung, cảnh báo nhàm...).
      onCampaignChange({ ...campaign, dailyBudgetCa: c.dailyBudgetCa });
      setDaily(c.dailyBudgetCa != null ? String(c.dailyBudgetCa) : '');
      toast.success(value === null ? 'Đã bỏ giới hạn ngân sách ngày.' : 'Đã lưu ngân sách ngày.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không lưu được ngân sách ngày.');
    } finally {
      budgetLock.current = false;
      setSavingBudget(false);
    }
  };

  const sendCreative = async () => {
    if (sendLock.current) return;
    if (!creative.landingUrl?.trim()) return void toast.error('Nhập link đích (khi bấm quảng cáo).');
    if (runsOnPreRoll(campaign.format, campaign.targetPlacements) && !/^https:\/\//i.test(creative.videoUrl || ''))
      return void toast.error('Chiến dịch chèn trước video cần một video. Bạn hãy tải video lên trước khi gửi.');
    sendLock.current = true;
    setSending(true);
    try {
      await addAdCreative(campaign.id, {
        title: creative.title,
        body: creative.body,
        imageUrl: creative.imageUrl,
        videoUrl: creative.videoUrl,
        landingUrl: creative.landingUrl!.trim(),
        ctaText: creative.ctaText,
      });
      toast.success('Đã gửi nội dung mới. Quảng cáo hiện tại vẫn chạy cho đến khi nội dung mới được duyệt.');
      onCreativeSent();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không gửi được nội dung mới.');
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  };

  const isPending = pending?.reviewStatus === 'Pending';
  const rejected = pending?.reviewStatus === 'Rejected' ? pending : null;

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="text-sm font-semibold ui-text">Ngân sách ngày</div>
        <p className="text-xs ui-muted">
          Tổng ngân sách {formatCa(campaign.totalBudgetCa)}, đã tiêu {formatCa(campaign.spentCa)}, còn{' '}
          {formatCa(ceiling)}. Ngân sách ngày từ {formatCa(MIN_DAILY)} đến {formatCa(ceiling)}. Để trống nếu
          không giới hạn theo ngày.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs ui-muted flex-1 min-w-[180px]">
            Ngân sách mỗi ngày (Ca)
            <input
              type="text"
              inputMode="numeric"
              className={inputCls}
              value={daily}
              disabled={savingBudget || ended}
              onChange={(e) => setDaily(digitsOnly(e.target.value))}
              placeholder="Không giới hạn"
            />
          </label>
          <button
            type="button"
            disabled={savingBudget || ended || daily.trim() === ''}
            onClick={() => {
              const v = parseWholeCa(daily);
              if (v === null) return void toast.error('Ngân sách ngày phải là số Ca nguyên.');
              saveBudget(v);
            }}
            className="rounded-md bg-[#0071A6] text-white text-sm px-4 py-2 disabled:opacity-50"
          >
            {savingBudget ? 'Đang lưu…' : 'Lưu'}
          </button>
          {campaign.dailyBudgetCa != null && (
            <button
              type="button"
              disabled={savingBudget || ended}
              onClick={() => saveBudget(null)}
              className="rounded-md ui-divider border text-sm px-4 py-2 ui-text disabled:opacity-50"
            >
              Bỏ giới hạn ngày
            </button>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-sm font-semibold ui-text">Thay nội dung quảng cáo</div>
        {isPending ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm ui-text">
            Nội dung mới đang chờ admin duyệt. Quảng cáo hiện tại vẫn chạy bình thường. Bạn chỉ gửi được nội
            dung khác sau khi nội dung này được xử lý.
            {pending && (
              <div className="mt-2 max-w-sm">
                <AdPreview
                  title={pending.title || undefined}
                  body={pending.body || undefined}
                  imageUrl={pending.imageUrl || undefined}
                  videoUrl={pending.videoUrl || undefined}
                  ctaText={pending.ctaText || undefined}
                  format={campaign.format}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {rejected && (
              <div className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-800 p-3 text-sm ui-text">
                Nội dung gửi gần nhất chưa được duyệt
                {rejected.rejectReason ? `. Lý do: ${rejected.rejectReason}` : '.'} Bạn có thể chỉnh lại và gửi
                bản khác.
              </div>
            )}
            {ended ? (
              <p className="text-sm ui-muted">Chiến dịch đã kết thúc, không thay nội dung được nữa.</p>
            ) : (
              <>
                <AdCreativeForm
                  showLanding
                  value={creative}
                  onChange={(patch) => setCreative((c) => ({ ...c, ...patch }))}
                  disabled={sending}
                  homeBannerRatio={runsOnHomeBanner(campaign.format, campaign.targetPlacements, campaign.creatorDistribution)}
                  allowVideo={campaign.format === 'Video'}
                />
                <div>
                  <div className="text-xs ui-muted mb-1">Xem trước</div>
                  <div className="max-w-sm">
                    <AdPreview
                      title={creative.title}
                      body={creative.body}
                      imageUrl={creative.imageUrl}
                      videoUrl={creative.videoUrl}
                      ctaText={creative.ctaText}
                      format={campaign.format}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  disabled={sending}
                  onClick={sendCreative}
                  className="rounded-md bg-[#0071A6] text-white text-sm px-4 py-2 disabled:opacity-50"
                >
                  {sending ? 'Đang gửi…' : 'Gửi nội dung mới để duyệt'}
                </button>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default AdBudgetCreativePanel;
